import React from 'react';
import Flexbox from 'flexbox-react';
import { Spinner, Tab, Tabs } from '@blueprintjs/core';
import { inject, observer } from 'mobx-react';
import ManageAccessPanel from './AccountSettingManageAccess';
import UsersJobProfile from './UsersJobProfile';

@inject('AccountUsersStore', 'ToastStore', 'CurrentUserStore')
@observer
export default class AccountUsersTabs extends React.Component {
  jobProfileLoading = false;
  constructor(props) {
    super(props);
    this.isLoadingUsers = false;
  }

  allowedAccountIds = [
    '6804890a-3257-46ed-a84a-d6ac156b0573'
  ];

  canManageAccess = () => {
    try {
      const { currentUser } = this.props.CurrentUserStore || {};
      if (!currentUser) return false;

      const isAccountOwner = !!currentUser.isAccountOwner;
      const accountId = currentUser?.accountId;

      return isAccountOwner && this.allowedAccountIds.includes(accountId);
    } catch (error) {
      return false;
    }
  };

  handleTabChange = (tab) => {
    const { AccountUsersStore } = this.props;

    if (tab === 'dataview') {
      window.location = `/account/users?tab=dataview`;
      return;
    }

    AccountUsersStore?.setActiveTab(tab);

    if (tab === 'users' || tab === 'jobProfile') {
      if (!this.jobProfileLoading) {
        this.jobProfileLoading = true;

        AccountUsersStore
          ?.fetchJobProfiles()
          .finally(() => {
            this.jobProfileLoading = false;
          });
      }
    }
  };

  renderUsersTitle = () => <span>{I18n.t('js.users')}</span>;

  renderRequestsTitle = () => <span>{I18n.t('js.requests')}</span>;

  renderInvitationsTitle = () => <span>{I18n.t('js.invitations')}</span>;

  renderImportTitle = () => <span>{I18n.t('js.import_users')}</span>;

  renderDataViewTitle = () => <span>{I18n.t('js.data')}</span>;

  renderJobProfileTitle = () => <span>Job Profiles</span>;

  renderQuestionCategoriesTitle = () => <span>Departments</span>;

  componentDidMount() {
    this.checkAndLoadUsers();

    const { AccountUsersStore } = this.props;

    if (
      (AccountUsersStore?.activeTab === 'users' ||
        AccountUsersStore?.activeTab === 'jobProfile') &&
      !this.jobProfileLoading
    ) {
      this.jobProfileLoading = true;

      AccountUsersStore.fetchJobProfiles()
        .finally(() => {
          this.jobProfileLoading = false;
        });
    }
  }

  componentDidUpdate(prevProps) {
    const { AccountUsersStore } = this.props;
    const wasManageAccessTab = prevProps.AccountUsersStore?.activeTab === 'manageAccess';
    const isManageAccessTab = AccountUsersStore?.activeTab === 'manageAccess';

    if (isManageAccessTab && !wasManageAccessTab) {
      this.checkAndLoadUsers();
    }
  }

  checkAndLoadUsers = () => {
    const { AccountUsersStore } = this.props;
    const isManageAccessTab = AccountUsersStore?.activeTab === 'manageAccess';
    const users = AccountUsersStore?.users || [];
    const hasUsers = Array.isArray(users) && users.length > 0;

    if (isManageAccessTab && !hasUsers && !this.isLoadingUsers) {
      this.loadUsers();
    }
  };

  loadUsers = () => {
    const { AccountUsersStore } = this.props;
    if (!AccountUsersStore?.fetchUsers || this.isLoadingUsers) {
      return;
    }

    this.isLoadingUsers = true;
    const pageNumber = 1;
    const perPage = AccountUsersStore.pagination?.xPerPage || 20;
    const offset = AccountUsersStore.pagination?.xOffset || 0;
    const query = '';
    const importId = '';

    AccountUsersStore.fetchUsers(pageNumber, perPage, offset, query, importId)
      .then(() => {
        this.isLoadingUsers = false;
      })
      .catch(error => {
        this.isLoadingUsers = false;
        if (error?.response?.data?.error) {
          console.error('Error loading users:', error.response.data.error);
        } else if (error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
          console.error('Error loading users:', error);
        }
      });
  };

  renderManageAccessPanel() {
    const { AccountUsersStore, ToastStore } = this.props;
    const users = AccountUsersStore?.users || [];

    const handleRemoveUser = (userId) => {
      if (AccountUsersStore?.removeUser) {
        AccountUsersStore.removeUser(userId);
      } else {
        ToastStore?.show?.(`Remove user: ${userId}`);
      }
    };

    const handleSendInvitation = (userId) => {
      if (AccountUsersStore?.sendInvitation) {
        AccountUsersStore.sendInvitation(userId);
      } else {
        ToastStore?.show?.(`Send invitation: ${userId}`);
      }
    };

    const handleToggleOwner = (userId, isOwner) => {
      if (AccountUsersStore?.toggleOwner) {
        AccountUsersStore.toggleOwner(userId, isOwner);
      } else {
        ToastStore?.show?.(`Toggle owner: ${userId} -> ${isOwner}`);
      }
    };

    if (users.length === 0) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spinner size={30} />
          <div style={{ marginTop: 12, color: '#666' }}>Loading users...</div>
        </div>
      );
    }

    return (
      <ManageAccessPanel
        users={users}
        onRemoveMember={handleRemoveUser}
        onSendInvitation={handleSendInvitation}
        onToggleOwner={handleToggleOwner}
      />
    );
  }

  renderTabs() {
    const { AccountUsersStore, loading } = this.props;

    if (loading) return <Spinner size={20} />;
    const hasManageAccessPermission = this.canManageAccess();

    return (
      <Tabs
        id="accountUsersTabnav"
        selectedTabId={AccountUsersStore?.activeTab}
        onChange={this.handleTabChange}
        renderActiveTabPanelOnly
      >
        <Tab id="users" title={this.renderUsersTitle()} />
        <Tab id="requests" title={this.renderRequestsTitle()} />
        <Tab id="invitations" title={this.renderInvitationsTitle()} />
        <Tab id="import" title={this.renderImportTitle()} />
        <Tab id="dataview" title={this.renderDataViewTitle()} />
        {hasManageAccessPermission && <Tab id="manageAccess" title="Manage Access" />}
        <Tab id="jobProfile" title={this.renderJobProfileTitle()} />
        <Tab id="questionCategories" title={this.renderQuestionCategoriesTitle()} />
        <Tabs.Expander />
      </Tabs>
    );
  }

  render() {
    const { AccountUsersStore } = this.props;
    const hasManageAccessPermission = this.canManageAccess();
    const isManageAccessTab = AccountUsersStore?.activeTab === 'manageAccess';
    const isJobProfileTab = AccountUsersStore?.activeTab === 'jobProfile';

    return (
      <>
        <nav className="bp3-navbar bp3-navbar-bordered">
          <Flexbox className="fixed-width-container">
            <Flexbox className="bp3-navbar-group bp3-align-left" flexGrow={1}>
              {this.renderTabs()}
            </Flexbox>
          </Flexbox>
        </nav>

        {hasManageAccessPermission && isManageAccessTab && this.renderManageAccessPanel()}
        {isJobProfileTab && <UsersJobProfile />}
      </>
    );
  }
}
